import { PrimitiveType } from "@buildwithflux/models";
import intersects from "intersects";
import { LRUCache } from "lru-cache";
import { makeCCW, quickDecomp } from "poly-decomp-es";
import { DecompMaxLevel, mat3 } from "../../../common";
import { pointDistance } from "../queries/distance";
import { approxFittingPolygon } from "./approxFittingPolygon";
import { isConvex } from "./isConvex";
import { chainCompoundPrimitiveTransforms } from "./utils";
const overlapCache = new LRUCache({
    ttl: 1000 * 60 * 5, // 5 min
    updateAgeOnGet: true,
    max: 1000 * 1000,
});
// TOOD: now difference shapes are supported by approxFittingPolygon, but they are still excluded here
// since it uses Clipper and it could be quite slow, evaluate if it's worth it to include them
function polygonsIntersectConvex(convexA, convexB) {
    const flatPointsA = convexA.flatMap((p) => [p.x, p.y]);
    const flatPointsB = convexB.flatMap((p) => [p.x, p.y]);
    return intersects.polygonPolygon(flatPointsA, flatPointsB);
}
function polygonsIntersectPartialConvex(convex, nonConvex) {
    const flatPointsA = convex.flatMap((p) => [p.x, p.y]);
    const pointsB = nonConvex.map((p) => [p.x, p.y]);
    makeCCW(pointsB);
    const convexPolygonsB = quickDecomp(pointsB, undefined, undefined, undefined, undefined, DecompMaxLevel);
    return convexPolygonsB.some((polyB) => intersects.polygonPolygon(flatPointsA, polyB.flatMap((p) => [p[0], p[1]])));
}
// Much more expensive but necessary for non-convex shapes, evaluate later if too slow
function polygonsIntersectGeneral(polygonA, polygonB) {
    const pointsA = polygonA.map((p) => [p.x, p.y]);
    const pointsB = polygonB.map((p) => [p.x, p.y]);
    makeCCW(pointsA);
    makeCCW(pointsB);
    const convexPolygonsA = quickDecomp(pointsA, undefined, undefined, undefined, undefined, DecompMaxLevel);
    const convexPolygonsB = quickDecomp(pointsB, undefined, undefined, undefined, undefined, DecompMaxLevel);
    return convexPolygonsA.some((polyA) => convexPolygonsB.some((polyB) => intersects.polygonPolygon(polyA.flatMap((p) => [p[0], p[1]]), polyB.flatMap((p) => [p[0], p[1]]))));
}
// Wrapper for isOverlapping that approximates the result difference shapes, by considering only the outer primitive
export function isOverlappingApprox(primitiveA, primitiveB, polygonApproxSegments = 64) {
    if (primitiveA.type === PrimitiveType.Difference) {
        // We discard the subtracted primitive (approximation)
        const [transformedA, _transformedB] = chainCompoundPrimitiveTransforms(primitiveA);
        return isOverlappingApprox(transformedA, primitiveB, polygonApproxSegments);
    }
    if (primitiveB.type === PrimitiveType.Difference) {
        // We discard the subtracted primitive (approximation)
        const [transformedA, _transformedB] = chainCompoundPrimitiveTransforms(primitiveB);
        return isOverlappingApprox(transformedA, primitiveA, polygonApproxSegments);
    }
    return isOverlapping(primitiveA, primitiveB, polygonApproxSegments);
}
// Doesn't yet support difference shapes!
export function isOverlapping(primitiveA, primitiveB, polygonApproxSegments = 64) {
    // A composite kep for overlap cache that preserves the order of the primitives
    // of the comparator pair
    const key = primitiveA.cacheKey && primitiveB.cacheKey
        ? primitiveA.cacheKey < primitiveB.cacheKey
            ? `${primitiveA.cacheKey}-${primitiveB.cacheKey}`
            : `${primitiveB.cacheKey}-${primitiveA.cacheKey}`
        : undefined;
    // Since primitive cache keys are optional, first check the key has been computed before
    // attempting a cache lookup. If a lookup is defined, return that to avoid more costly
    // overlap computation
    if (key !== undefined) {
        const value = overlapCache.get(key);
        if (value !== undefined) {
            return value;
        }
    }
    if (primitiveA.type === PrimitiveType.Difference || primitiveB.type === PrimitiveType.Difference) {
        throw new Error("Difference shapes are not yet supported in isOverlapping");
    }
    // Recurses over compound shapes
    if (primitiveA.type === PrimitiveType.Union) {
        const [transformedA, transformedB] = chainCompoundPrimitiveTransforms(primitiveA);
        const resultA = isOverlapping(transformedA, primitiveB, polygonApproxSegments);
        const resultB = isOverlapping(transformedB, primitiveB, polygonApproxSegments);
        return resultA || resultB;
    }
    if (primitiveB.type === PrimitiveType.Union) {
        const [transformedA, transformedB] = chainCompoundPrimitiveTransforms(primitiveB);
        const resultA = isOverlapping(transformedA, primitiveA, polygonApproxSegments);
        const resultB = isOverlapping(transformedB, primitiveA, polygonApproxSegments);
        return resultA || resultB;
    }
    if (primitiveA.type === PrimitiveType.Circle) {
        const transformDecomposed = mat3.decompose(primitiveA.transform);
        const transformIsSimple = mat3.affineSanityCheck(primitiveA.transform) &&
            transformDecomposed.scaleIsEqual &&
            transformDecomposed.xyDot === 0;
        if (transformIsSimple) {
            const { dist } = pointDistance(primitiveB, transformDecomposed.translation.x, transformDecomposed.translation.y);
            const radius = primitiveA.radius * Math.abs(transformDecomposed.scale.x);
            return dist <= radius;
        }
    }
    if (primitiveB.type === PrimitiveType.Circle) {
        const transformDecomposed = mat3.decompose(primitiveB.transform);
        const transformIsSimple = mat3.affineSanityCheck(primitiveB.transform) &&
            transformDecomposed.scaleIsEqual &&
            transformDecomposed.xyDot === 0;
        if (transformIsSimple) {
            const { dist } = pointDistance(primitiveA, transformDecomposed.translation.x, transformDecomposed.translation.y);
            const radius = primitiveB.radius * Math.abs(transformDecomposed.scale.x);
            return dist <= radius;
        }
    }
    // Cache helper, stores the result of the boolean computation in the LRU cache
    // if a key is present. This function assumes prior attempt to retrieve value
    // from cache with no hit
    const cachedBoolean = (computeBoolean) => {
        const value = computeBoolean();
        if (key !== undefined) {
            overlapCache.set(key, value);
        }
        return value;
    };
    // Worst case: convert to polygons
    const polyA = approxFittingPolygon(primitiveA, polygonApproxSegments);
    const polyB = approxFittingPolygon(primitiveB, polygonApproxSegments);
    if (isConvex(primitiveA) && isConvex(primitiveB)) {
        return cachedBoolean(() => polygonsIntersectConvex(polyA, polyB));
    }
    if (isConvex(primitiveA)) {
        // Question: how much faster are these that the convex to convex test on average?
        return cachedBoolean(() => polygonsIntersectPartialConvex(polyA, polyB));
    }
    if (isConvex(primitiveB)) {
        // Question: how much faster are these that the convex to convex test on average?
        return cachedBoolean(() => polygonsIntersectPartialConvex(polyB, polyA));
    }
    return cachedBoolean(() => polygonsIntersectGeneral(polyA, polyB));
}
//# sourceMappingURL=isOverlapping.js.map